"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.searchProductServer = exports.getVariantInventory = exports.isDynamicBarcode = exports.getProductActionOrder = exports.isNonRevenue = exports.isDeposit = exports.isSoldByWeight = exports.isDynamicPrice = exports.hasInventory = exports.hasAddonGroups = exports.hasAddons = exports.hasVariants = exports.createCustomProduct = exports.getDepositPrice = exports.getProductPrice = exports.getProductDescription = exports.getVariantName = exports.getProductName = exports.PRODUCT_ACTION_ORDER = void 0;
const product_1 = require("../models/product");
const utils_1 = require("./utils");
exports.PRODUCT_ACTION_ORDER = {
    DEFAULT: 'DEFAULT',
    VARIANTS: 'VARIANTS',
    SOLD_BY_WEIGHT: 'SOLD_BY_WEIGHT',
    DYNAMIC: 'DYNAMIC',
    ADDONS: 'ADDONS',
    ADDON_GROUPS: 'ADDON_GROUPS',
    DEPOSIT: 'DEPOSIT',
    NON_REVENUE: 'NON_REVENUE'
};
/**
 * Return product's name translated (if it has translation) or the default product name
 * @param  {Product} product - product obj
 * @param  {string} language - language to be translated
 * @returns {string} translated product name
 */
const getProductName = (product, language = 'en') => {
    if (!!product.translations && !!product.translations.names) {
        return !!product.translations.names[language]
            ? product.translations.names[language]
            : product.name;
    }
    return product.name;
};
exports.getProductName = getProductName;
/**
 * Return variant's name translated (if it has translation) or the default variant name
 * @param  {Variant} variant - variant obj
 * @param  {string} language - language to be translated
 * @returns {string} translated variant name
 */
const getVariantName = (variant, language = 'en') => {
    if (!!variant.translations && !!variant.translations.names) {
        return !!variant.translations.names[language]
            ? variant.translations.names[language]
            : variant.name;
    }
    return variant.name;
};
exports.getVariantName = getVariantName;
/**
 * Return product's description translated (if it has translation) or the default product name
 * @param  {Product} product - product obj
 * @param  {string} language - language to be translated
 * @returns {string} translated product's description
 */
const getProductDescription = (product, language = 'en') => {
    if (!!product.translations && !!product.translations.descriptions) {
        return !!product.translations.descriptions[language]
            ? product.translations.descriptions[language]
            : product.description;
    }
    return !!product.description ? product.description : '';
};
exports.getProductDescription = getProductDescription;
/**
 * Return product price based on the store and variant
 * @param  {string} storeId - ObjectId of the store
 * @param  {Product} product - product obj
 * @param  {string} variantId - ObjectId of the variant
 * @param  {string} type - [price, cost_price]
 * @returns {number} product price
 */
const getProductPrice = (product, variantId, storeId, type = 'price') => {
    const variantIndex = variantId
        ? product.variants.findIndex((v) => v._id === variantId)
        : 0;
    const variant = variantIndex >= 0 ? product.variants[variantIndex] : product.variants[0];
    if (variant.local_prices && variant.local_prices.length) {
        const priceIndex = variant.local_prices.findIndex((local) => local.store === storeId);
        if (priceIndex >= 0) {
            return variant.local_prices[priceIndex][type] ? variant.local_prices[priceIndex][type] : 0;
        }
    }
    const result = type === 'price' ? variant.price : variant.cost_price;
    return !!result ? result : 0;
};
exports.getProductPrice = getProductPrice;
/**
 * Return product's deposit price (or 0 if it doesn't have)
 * @param  {Product} product - product obj
 * @returns {number} Deposit price
 */
const getDepositPrice = (product) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = product.options) === null || _a === void 0 ? void 0 : _a.deposit) === null || _b === void 0 ? void 0 : _b.price) !== null && _c !== void 0 ? _c : 0;
};
exports.getDepositPrice = getDepositPrice;
/**
 * Create custom product obj
 * @param  {string} name - new name for custom product or the default
 * @returns {any} custom product obj
 */
const createCustomProduct = (name) => {
    const custom = {
        _id: (0, utils_1.ObjectId)(),
        name: !!name ? name : 'Custom product',
        image: '',
        price: 0,
        addons: [],
        variants: [
            {
                price: 0,
                const_price: 0,
                name: 'Default',
                _id: (0, utils_1.ObjectId)(),
                tax: {},
                ean: '',
                sku: '',
                local_prices: [],
                parts: [],
                default: true
            }
        ],
        options: {
            is_custom: true,
            stock: 'none'
        },
        inventory: [],
        categories: []
    };
    return custom;
};
exports.createCustomProduct = createCustomProduct;
/**
 * Return if product has multiple variants
 * @param  {Product} product - Product obj
 * @returns {boolean} true if product has multiple variants
 */
const hasVariants = (product) => {
    return !!product.variants.length && product.variants.length > 1;
};
exports.hasVariants = hasVariants;
/**
 * Return if product has addons
 * @param  {Product} product - Product obj
 * @returns {boolean} true if product has addons
 */
const hasAddons = (product) => {
    return !!product.addons && !!product.addons.length;
};
exports.hasAddons = hasAddons;
/**
 * Return if product has addonGroups
 * @param  {Product} product - Product obj
 * @returns {boolean} true if product has addonGroups
 */
const hasAddonGroups = (product) => {
    return !!product.addonGroups && !!product.addonGroups.length;
};
exports.hasAddonGroups = hasAddonGroups;
/**
 * Return if product has inventory
 * @param  {Product} product - Product obj
 * @returns {boolean} true if product has inventory
 */
const hasInventory = (product) => {
    const { inventory, options } = product;
    return (!!options &&
        !!options.stock &&
        options.stock === product_1.StockOptions.SELF &&
        !!inventory &&
        !!inventory.length);
};
exports.hasInventory = hasInventory;
/**
 * Return if product is dynamic price
 * @param  {Product} product - Product obj
 * @returns {boolean} true if product has dynamic price
 */
const isDynamicPrice = (product) => {
    return !!product.options && !!product.options.dynamic_pricing;
};
exports.isDynamicPrice = isDynamicPrice;
/**
 * Return if product is sold by weight
 * @param  {Product} product - Product obj
 * @returns {boolean} true if product is sold by weight
 */
const isSoldByWeight = (product) => {
    return !!product.options && !!product.options.sold_by_weight;
};
exports.isSoldByWeight = isSoldByWeight;
/**
 * Return if product is has deposit
 * @param  {Product} product - Product obj
 * @returns {boolean} true if product has depoist
 */
const isDeposit = (product) => {
    const deposit = !!product.options && product.options.deposit;
    return !!deposit && !!deposit.price;
};
exports.isDeposit = isDeposit;
/**
 * Return if product is non revenue
 * @param  {Product} product - Product obj
 * @returns {boolean} true if product is non revenue
 */
const isNonRevenue = (product) => {
    return !!product.options && !!product.options.non_revenue;
};
exports.isNonRevenue = isNonRevenue;
/**
 * Return the order that a product should be threat before it goes into cart
 * @param  {Product} product - Product obj
 * @param  {boolean} deposit - If this product should be threat as deposit (add deposit value * -1)
 * @returns {string[]} Array of string contained the order how show apply the actions to a product
 */
const getProductActionOrder = (product, deposit = false) => {
    const order = [];
    if (deposit && (0, exports.isDeposit)(product)) {
        order.push(exports.PRODUCT_ACTION_ORDER.DEPOSIT);
        return order;
    }
    if ((0, exports.hasVariants)(product)) {
        order.push(exports.PRODUCT_ACTION_ORDER.VARIANTS);
    }
    if ((0, exports.isNonRevenue)(product)) {
        order.push(exports.PRODUCT_ACTION_ORDER.NON_REVENUE);
    }
    if ((0, exports.isSoldByWeight)(product)) {
        order.push(exports.PRODUCT_ACTION_ORDER.SOLD_BY_WEIGHT);
    }
    if ((0, exports.isDynamicPrice)(product)) {
        order.push(exports.PRODUCT_ACTION_ORDER.DYNAMIC);
    }
    if ((0, exports.hasAddons)(product) || (0, exports.hasAddonGroups)(product)) {
        order.push(exports.PRODUCT_ACTION_ORDER.ADDONS);
    }
    if (!order.length) {
        order.push(exports.PRODUCT_ACTION_ORDER.DEFAULT);
    }
    return order;
};
exports.getProductActionOrder = getProductActionOrder;
/**
 * Check if product has dynamic barcdoe and add new price if it does
 * @param  {Product} product - Product obj
 * @param  {string} ean - Scanned barcode
 * @returns {Product | null} returns if dynamic barcode the product with updated price
 */
const isDynamicBarcode = (product, ean) => {
    const prodCopy = JSON.parse(JSON.stringify(product));
    // if first digit of ena is '2' or '5' and product has dynamic pricing then calculate the price
    // from the last 5 digits of the ean (ignoring the last digit)
    if (ean.length >= 10 &&
        (ean[0] === '2' || ean[0] === '5') &&
        (prodCopy.options || {}).dynamic_pricing) {
        const calculated_price = (0, utils_1.formatCurrency)(ean.slice(-5, ean.length - 1) / 100);
        prodCopy.variants[0].price = !isNaN(calculated_price)
            ? parseFloat(calculated_price)
            : parseFloat(prodCopy.variants[0].price);
        return prodCopy;
    }
    else {
        return null;
    }
};
exports.isDynamicBarcode = isDynamicBarcode;
/**
 * Get stock number of product's variant (if exits)
 * @param  {Product} product - Product obj
 * @param  {string} variantId - Variant id
 * @param  {string} storeId - Store id
 * @returns {number} returns stock value of variant of the product with desired store
 */
const getVariantInventory = (product, variantId, storeId) => {
    if (!!product && !!(0, exports.hasInventory)(product)) {
        const { inventory } = product;
        const inventoryIndex = inventory.findIndex((inv) => inv.store === storeId);
        if (inventoryIndex >= 0) {
            const inv = inventory[inventoryIndex];
            return inv.units && inv.units[variantId] ? inv.units[variantId] : 0;
        }
    }
    return 0;
};
exports.getVariantInventory = getVariantInventory;
/**
 * Search product server-side by name or ean
 * @param  {CountrSdk} sdk - Countr sdk obj
 * @param  {string} value - Text to be searched for
 * @returns {Promise<Product[]>}  returns array of products or empty array
 */
const searchProductServer = (countr, value) => {
    if ('products' in countr && !!countr.products.search) {
        return value.length > 3 ? countr.products.search({ text: value }) : Promise.resolve([]);
    }
    throw Error('Countr instance passed to searchProductServer is not valid');
};
exports.searchProductServer = searchProductServer;
